import React, {useEffect, useRef, useState} from 'react';
import DefaultButton from 'components/common/DefaultButton';
import Footer from 'components/common/Footer';
import NarrowHeader from 'components/common/NarrowHeader';
import ScreenLayout from 'components/common/ScreenLayout';
import styled, {css} from 'styled-components';
import {colors} from 'styles/colors';
import {getDevice} from 'utils/getDevice';
import {useLocation, useNavigate} from 'react-router-dom';
import CommonModal from 'components/common/CommonModal';
import {ICountryArray, countryArray} from 'data/country';
import {minmax} from 'utils/minmax';
import PhoneVerification from 'components/register/PhoneVerification';
import PasswordSetting from 'components/register/PasswordSetting';
import ExtraInfo from 'components/register/ExtraInfo';
import RegisterTerms from 'components/register/RegisterTerms';
import RegisterSignature from 'components/register/RegisterSignature';
import {buttonAction, doLogin, updateExistingKeys} from 'function/converter';
import {userInfoState} from 'utils/Atoms';
import {useRecoilState} from 'recoil';

/**
 * 회원가입 정보 타입
 * @property {string} phone - 전화번호
 * @property {string} verifiCode - 인증 코드
 * @property {string} password - 비밀번호
 * @property {string} checkPassword - 비밀번호 확인
 * @property {string} name - 이름
 * @property {string} birthDay - 생년월일
 * @property {string} gender - 성별
 * @property {string} nationality - 국적
 * @property {boolean} service - 서비스 약관 동의 여부
 * @property {boolean} privacy - 개인정보 약관 동의 여부
 * @property {boolean} location - 위치기반 약관 동의 여부
 */
export type IRegisterInfo = {
  [key: string]: any;
  phone: string;
  verifiCode: string;
  password: string;
  checkPassword: string;
  name: string;
  birthDay: string;
  gender: string;
  nationality: string;
  service: boolean; // 서비스 약관
  privacy: boolean; // 개인정보 약관
  location: boolean; // 위치기반 약관
};

/**
 * 에러 텍스트 타입
 * 각 필드별 에러 메시지를 저장합니다.
 * @property {string} phone - 전화번호 에러 메시지
 * @property {string} verifiCode - 인증 코드 에러 메시지
 * @property {string} password - 비밀번호 에러 메시지
 * @property {string} checkPassword - 비밀번호 확인 에러 메시지
 * @property {string} name - 이름 에러 메시지
 * @property {string} birthDay - 생년월일 에러 메시지
 * @property {string} gender - 성별 에러 메시지
 * @property {string} nationality - 국적 에러 메시지
 * @property {string} service - 서비스 약관 에러 메시지
 * @property {string} privacy - 개인정보 약관 에러 메시지
 * @property {string} location - 위치기반 약관 에러 메시지
 */
export type IErrorTexts = {
  [key: string]: any;
  phone: string;
  verifiCode: string;
  password: string;
  checkPassword: string;
  name: string;
  birthDay: string;
  gender: string;
  nationality: string;
  service: string; // 서비스 약관
  privacy: string; // 개인정보 약관
  location: string; // 위치기반 약관
};

/**
 * 모달 상태 타입
 * 각 모달의 상태와 열림 여부를 관리합니다.
 * @property {string} state - 모달 상태
 * @property {boolean} open - 모달 열림 여부
 * @property {object} [info] - 모달에 전달할 추가 정보
 */
export type IModalState = {
  [key: string]: {state: string; open: boolean; info?: {}};
};

/**
 * 초기 회원가입 정보
 * 회원가입 폼의 초기값을 정의합니다.
 */
const initialRegister = {
  phone: '',
  verifiCode: '',
  password: '',
  checkPassword: '',
  name: '',
  birthDay: '',
  gender: 'MAN',
  nationality: 'KR',
  service: false, // 서비스 약관
  privacy: false, // 개인정보 약관
  location: false, // 위치기반 약관
};

/**
 * 회원가입 페이지 컴포넌트
 * 단계별 회원가입 프로세스를 처리하는 페이지입니다.
 * @returns {JSX.Element} 회원가입 페이지 컴포넌트
 */
const Register = () => {
  const device = getDevice();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState({});
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);

  const [selectCountry, setSelectCountry] = useState<ICountryArray>(
    countryArray[0],
  );

  const [registerInfo, setRegisterInfo] = useState<IRegisterInfo>({
    ...initialRegister,
  });

  // 날짜 모달
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [onDatePicker, setOnDatePicker] = useState<boolean>(false);

  // 모달
  const initModalState: IModalState = {
    confirm: {state: '', open: false},
    tempInfo: {state: '', open: false},
  };
  const [modalState, setModalState] = useState<IModalState>(initModalState);

  // 다음으로 or 가입하기 활성화
  const [enableButton, setEnableButton] = useState(false);

  // 인증번호 전송 버튼 텍스트
  const [authSendButtonTitle, setAuthSendButtonTitle] = useState('sendAuth');

  // 다음으로 or 가입하기 버튼 스타일
  const buttonStyle = {
    width: '100%',
    height: '60px',
    backgroundColor: enableButton ? colors.primary2 : '#ededed',
  };

  // 인증 코드 인증 여부
  const [checkedAuth, setCheckedAuth] = useState<boolean>(false);

  // 서명 이미지
  const [signature, setSignature] = useState<Blob>();
  const [signatureThumbnail, setSignatureThumbnail] = useState<Blob>();

  const location = useLocation();
  useEffect(() => {
    if (userInfo) {
      navigate('/');
      return;
    }
    const step = location?.state?.step;
    const info = location?.state?.info;
    if (step) {
      setStep(step);
      const newObject = updateExistingKeys(registerInfo, info);
      setRegisterInfo(newObject);
    } else {
      // Direct URL access without state from Login page — block it
      navigate('/');
      return;
    }
  }, []);

  // step
  const [step, setStep] = useState(0);
  const steps = [
    {
      // 휴대폰 번호 입력
      title: 'mobilePhoneVerification',
      component: (
        <PhoneVerification
          key={0}
          selectCountry={selectCountry}
          setSelectCountry={setSelectCountry}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
          setEnableButton={setEnableButton}
          setAuthSendButtonTitle={setAuthSendButtonTitle}
          authSendButtonTitle={authSendButtonTitle}
          codePurpose="SIGN_UP"
          setModalState={setModalState}
          setCheckedAuth={setCheckedAuth}
          checkedAuth={checkedAuth}
        />
      ),
    },
    {
      // 비밀번호 설정
      title: 'setPassword',
      component: (
        <PasswordSetting
          key={1}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setEnableButton={setEnableButton}
        />
      ),
    },
    {
      // 그 외 정보 설정
      title: 'enterExtraInformation',
      component: (
        <ExtraInfo
          key={2}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setOnDatePicker={setOnDatePicker}
          onDatePicker={onDatePicker}
          datePickerRef={datePickerRef}
          setEnableButton={setEnableButton}
        />
      ),
    },
    {
      // 약관 동의 설정
      title: 'agreeTerms',
      component: (
        <RegisterTerms
          key={3}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setEnableButton={setEnableButton}
        />
      ),
    },
    {
      // 서명 등록
      title: 'signatureRegistration',
      component: (
        <RegisterSignature
          key={4}
          setEnableButton={setEnableButton}
          setSignature={setSignature}
          setSignatureThumbnail={setSignatureThumbnail}
        />
      ),
    },
  ];

  return (
    <ScreenLayout isLoading={isLoading} noHeader={true} errorModal={errorModal}>
      <Container $device={device}>
        <Main $device={device}>
          <InputField>
            {/* 헤더 */}
            <NarrowHeader title={steps[step].title} />
            {/* 정보 입력 컴포넌트 */}
            <InputContainer step={step}>{steps[step].component}</InputContainer>
          </InputField>

          {/* 다음으로 or 가입하기 버튼 */}
          <DefaultButton
            text={step !== 4 ? 'next' : 'toSignUp'}
            buttonStyle={buttonStyle}
            disabled={!enableButton}
            textStyle={{
              color: enableButton ? colors.white : colors.borderGray,
              fontSize: minmax(20, 16),
            }}
            onClick={async () =>
              await buttonAction({
                enableButton,
                step,
                selectCountry,
                registerInfo,
                setErrorModal,
                setIsLoading,
                setStep,
                setEnableButton,
                signature: signature,
                signature_thumbnail: signatureThumbnail,
                setModalState,
              })
            }
          />
        </Main>
      </Container>
      <Footer isNarrow />

      {/* 회원가입 완료 모달 */}
      {modalState.confirm.open && (
        <CommonModal
          title={'welcome'}
          detail={'completeRegisterDetail'}
          firstButtonText={'confirm'}
          firstButtonClick={async () => {
            await doLogin({
              setIsLoading,
              password: registerInfo.password,
              id: registerInfo.phone,
              setUserInfo,
              autoLogin: false,
              setErrorModal,
              setModalState,
            });
            navigate('/settingSite');
          }}
          closeModal={async () => {
            await doLogin({
              setIsLoading,
              password: registerInfo.password,
              id: registerInfo.phone,
              setUserInfo,
              autoLogin: false,
              setErrorModal,
              setModalState,
            });
            navigate('/settingSite');
          }}
        />
      )}

      {/* 작성 중인 화면으로 이동 모달 */}
      {modalState.tempInfo.open && (
        <CommonModal
          title={'tempInfoModalTitle'}
          detail={'tempInfoModalDetail'}
          firstButtonText={'confirm'}
          firstButtonClick={() => {
            if (modalState.tempInfo.state === 'NEED_PASSWORD_SET') {
              setStep(1);
            } else if (modalState.tempInfo.state === 'ACCOUNT_INITIALIZED') {
              setStep(2);
            } else if (
              modalState.tempInfo.state === 'PROFILE_SETTING_INITIALIZED'
            ) {
              setStep(3);
            } else if (
              modalState.tempInfo.state === 'TERMS_OF_SERVICE_AGREEMENT'
            ) {
              setStep(4);
            }
            setModalState(initModalState);
          }}
        />
      )}
    </ScreenLayout>
  );
};

export default Register;

const Container = styled.div<{$device?: string}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
`;

const Main = styled.div<{$device?: string}>`
  display: flex;
  width: ${minmax(680)};
  flex-direction: column;
  align-items: center;

  ${(props: any) =>
    props.$device === 'mobile' &&
    css`
      width: 90%;
    `}
`;

const InputField = styled.div<{$device?: string}>`
  width: 100%;
  margin-bottom: 40px;
  padding-top: 34px;
  padding-bottom: max(20px, min(2vw, 40px));
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(10)};
  margin-top: ${minmax(30)};
`;

const InputContainer = styled.div<{step?: number}>`
  margin: ${props =>
    props.step === 4
      ? 0
      : 'max(20px, min(2vw, 40px)) max(20px, min(2vw, 40px)) 0'};
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  input:focus {
    border: ${props => (props.step === 0 ? '1px solid #808080' : '')};
  }
`;
